/**
 * TipTap ↔ constrained-HTML parsers (the read direction).
 *
 * `telegram/tiptap-to-telegram-html.ts` owns the WRITE direction for Telegram
 * message bodies. This module owns:
 *
 *  - `telegramHtmlToTipTap` — the missing inverse of that serializer. Without
 *    it a stored message re-opened in the composer arrived as flat text with
 *    its bold/italic runs and `{firstName}` chips destroyed (the round-trip
 *    caveat flagged on `NotificationsSection`).
 *  - the Preparation pair — `tiptapToPreparationHtml` / `preparationHtmlToTipTap`
 *    plus `preparationHtmlToPlainText`. Preparation notes are NOT a Telegram
 *    body: they are rendered as real HTML on the client booking page, so their
 *    serializer emits `<ul><li>` (Telegram's cannot) and its output must survive
 *    the server sanitizer byte-for-byte.
 *
 * CONTRACT WITH THE SERVER SANITIZER
 * `sanitizePreparationNotes` (apps/api) allows b/strong/i/em/ul/li/font/br,
 * collapses consecutive `<br>`, strips leading/trailing `<br>`, and re-emits
 * breaks as `<br />`. `tiptapToPreparationHtml` therefore emits `<br />`,
 * never two in a row, and never at either end — so a save round-trips
 * unchanged instead of drifting on every write. A blank line between
 * paragraphs is NOT representable (the sanitizer collapses it); empty
 * paragraphs are dropped on the way out and never re-created on the way in.
 */

import type {
  TipTapDocument,
  TipTapMark,
  TipTapNode,
} from '../telegram/tiptap-to-telegram-html.js';

// =============================================================================
// Inline primitive — one tokenizer, two mark vocabularies
// =============================================================================

interface InlineParseOptions {
  /** Lowercased tag name → TipTap mark type. Unlisted tags are dropped, text kept. */
  markTags: Record<string, string>;
  /** Recognize the literal `{firstName}` token and emit the atom node for it. */
  firstNameAtom: boolean;
  /** Parse `<a href>` into a link mark (Telegram bodies only). */
  links: boolean;
  /** Parse `<span class="tg-spoiler">` into the tgSpoiler mark (Telegram bodies only). */
  spoilers: boolean;
}

const TELEGRAM_INLINE: InlineParseOptions = {
  markTags: {
    b: 'bold',
    strong: 'bold',
    i: 'italic',
    em: 'italic',
    u: 'underline',
    ins: 'underline',
    s: 'strike',
    strike: 'strike',
    del: 'strike',
    code: 'code',
  },
  firstNameAtom: true,
  links: true,
  spoilers: true,
};

const PREPARATION_INLINE: InlineParseOptions = {
  // Preparation has no personalization and no links: the sanitizer allows
  // exactly bold/italic (plus the block-level list handled above this layer).
  markTags: { b: 'bold', strong: 'bold', i: 'italic', em: 'italic' },
  firstNameAtom: false,
  links: false,
  spoilers: false,
};

const TAG_RE = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)((?:\s[^>]*?)?)\/?>/g;
const HREF_RE = /href\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i;
const SPOILER_RE = /class\s*=\s*("[^"]*tg-spoiler[^"]*"|'[^']*tg-spoiler[^']*')/i;

/**
 * Parse ONE line of constrained HTML into TipTap inline nodes. Unknown tags are
 * discarded but their text content is kept (a dropped `<span>` must not eat the
 * words inside it); entities are decoded last so `&lt;b&gt;` stays literal text
 * rather than re-entering the tokenizer as markup.
 */
function parseInline(source: string, opts: InlineParseOptions): TipTapNode[] {
  const out: TipTapNode[] = [];
  const markStack: TipTapMark[] = [];
  let cursor = 0;

  const pushText = (raw: string) => {
    if (!raw) return;
    const marks = markStack.length > 0 ? markStack.map((m) => ({ ...m })) : undefined;
    const segments = opts.firstNameAtom ? raw.split('{firstName}') : [raw];
    segments.forEach((segment, index) => {
      if (index > 0) out.push({ type: 'firstName' });
      const text = decodeEntities(segment);
      if (text) out.push(marks ? { type: 'text', text, marks } : { type: 'text', text });
    });
  };

  TAG_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TAG_RE.exec(source)) !== null) {
    pushText(source.slice(cursor, match.index));
    cursor = TAG_RE.lastIndex;

    const isClose = match[1] === '/';
    const tag = match[2].toLowerCase();
    const attrs = match[3] ?? '';
    const markType = resolveMarkType(tag, attrs, opts);
    if (!markType) continue;

    if (isClose) {
      for (let i = markStack.length - 1; i >= 0; i -= 1) {
        if (markStack[i].type === markType) {
          markStack.splice(i, 1);
          break;
        }
      }
      continue;
    }

    if (markType === 'link') {
      const href = HREF_RE.exec(attrs);
      markStack.push({
        type: 'link',
        attrs: { href: decodeEntities(href?.[2] ?? href?.[3] ?? href?.[4] ?? '') },
      });
      continue;
    }
    markStack.push({ type: markType });
  }
  pushText(source.slice(cursor));

  return out;
}

function resolveMarkType(tag: string, attrs: string, opts: InlineParseOptions): string | null {
  if (tag === 'a') return opts.links ? 'link' : null;
  if (tag === 'span') {
    if (!opts.spoilers) return null;
    // A bare/unknown <span> carries no mark; only the spoiler span does. On the
    // close tag the attributes are gone, so an unconditional 'tgSpoiler' is the
    // only way </span> can pop what <span class="tg-spoiler"> pushed.
    return attrs === '' || SPOILER_RE.test(attrs) ? 'tgSpoiler' : null;
  }
  return opts.markTags[tag] ?? null;
}

const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&nbsp;': ' ',
};

function decodeEntities(s: string): string {
  // `&amp;` last would double-decode (`&amp;lt;` → `<`); the single pass over an
  // alternation decodes each entity exactly once, left to right.
  return s.replace(/&(?:amp|lt|gt|quot|apos|nbsp|#39);/g, (e) => ENTITIES[e] ?? e);
}

function escapeText(s: string): string {
  return s.replace(/[&<>]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;'));
}

function emptyDoc(): TipTapDocument {
  return { type: 'doc', content: [{ type: 'paragraph' }] };
}

function paragraph(content: TipTapNode[]): TipTapNode {
  return content.length > 0 ? { type: 'paragraph', content } : { type: 'paragraph' };
}

// =============================================================================
// Telegram HTML → TipTap
// =============================================================================

/**
 * Inverse of `tiptapToTelegramHtml`. Newlines (the serializer's paragraph join)
 * and any legacy `<br>` both become paragraph boundaries; `{firstName}` returns
 * to an atom node so the composer renders it as a chip instead of leaking the
 * raw token into the admin's view.
 *
 * NOT loss-free by design: `<pre>` / `<blockquote>` wrappers flatten to plain
 * paragraphs (their text is preserved). Nothing in the notification slots
 * authors those.
 */
export function telegramHtmlToTipTap(html: string | null | undefined): TipTapDocument {
  if (!html) return emptyDoc();
  const lines = html.replace(/<br\s*\/?\s*>/gi, '\n').split('\n');
  const content = lines.map((line) => paragraph(parseInline(line, TELEGRAM_INLINE)));
  return { type: 'doc', content: content.length > 0 ? content : [{ type: 'paragraph' }] };
}

// =============================================================================
// Preparation notes ↔ TipTap
// =============================================================================

const UL_BLOCK_RE = /<ul[^>]*>([\s\S]*?)<\/ul\s*>/gi;
const LI_RE = /<li[^>]*>([\s\S]*?)<\/li\s*>/gi;

/**
 * Parse stored `preparationNotes` into a TipTap document.
 *
 * Handles BOTH shapes the column holds: the sanitizer-shaped HTML this module
 * writes, and the plain text with bare newlines written before the field became
 * rich (those lines simply become paragraphs).
 */
export function preparationHtmlToTipTap(html: string | null | undefined): TipTapDocument {
  if (!html || !html.trim()) return emptyDoc();

  const blocks: TipTapNode[] = [];
  let cursor = 0;
  UL_BLOCK_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = UL_BLOCK_RE.exec(html)) !== null) {
    blocks.push(...inlineSegmentToBlocks(html.slice(cursor, match.index)));
    const items = parseListItems(match[1]);
    if (items.length > 0) blocks.push({ type: 'bulletList', content: items });
    cursor = UL_BLOCK_RE.lastIndex;
  }
  blocks.push(...inlineSegmentToBlocks(html.slice(cursor)));

  return { type: 'doc', content: blocks.length > 0 ? blocks : [{ type: 'paragraph' }] };
}

function inlineSegmentToBlocks(segment: string): TipTapNode[] {
  if (!segment) return [];
  return segment
    .replace(/<br\s*\/?\s*>/gi, '\n')
    .split('\n')
    .map((line) => parseInline(line, PREPARATION_INLINE))
    .filter((nodes) => nodes.some((n) => n.type !== 'text' || (n.text ?? '').trim() !== ''))
    .map((nodes) => paragraph(nodes));
}

function parseListItems(inner: string): TipTapNode[] {
  const items: TipTapNode[] = [];
  LI_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = LI_RE.exec(inner)) !== null) {
    const nodes = parseListItemInline(match[1]);
    if (nodes.length === 0) continue;
    items.push({ type: 'listItem', content: [paragraph(nodes)] });
  }
  return items;
}

/**
 * One list item's inline content.
 *
 * A `<br />` INSIDE an `<li>` is a real authored break: Shift+Enter within a
 * bullet, which the serializer writes as `<li>line one<br />line two</li>`.
 * Flattening it to a space (what this used to do) meant the break survived the
 * save and then vanished the next time the note was opened. Splitting on it and
 * re-emitting `hardBreak` nodes is what closes that round trip.
 */
function parseListItemInline(inner: string): TipTapNode[] {
  const out: TipTapNode[] = [];
  inner.split(/<br\s*\/?\s*>/i).forEach((segment, index) => {
    if (index > 0) out.push({ type: 'hardBreak' });
    out.push(...parseInline(segment, PREPARATION_INLINE));
  });
  return out;
}

/**
 * Serialize a TipTap document to sanitizer-safe preparation HTML.
 *
 * Emits ONLY `<b>`, `<i>`, `<ul>`, `<li>`, `<br />`. Empty paragraphs are
 * dropped and no two `<br />` are ever adjacent, because the server sanitizer
 * collapses both — emitting them would make every save rewrite the field.
 */
export function tiptapToPreparationHtml(doc: TipTapDocument | null | undefined): string {
  if (!doc?.content) return '';

  const pieces: { inline: boolean; html: string }[] = [];
  for (const node of doc.content) {
    if (node.type === 'bulletList' || node.type === 'orderedList') {
      // Ordered lists fold into `<ul>`: the sanitizer rewrites `<ol>` to `<ul>`
      // anyway, so emitting `<ol>` would promise numbering the client never gets.
      const items = (node.content ?? [])
        .filter((item) => item.type === 'listItem')
        .map((item) => renderPrepBlockChildren(item.content))
        .filter((s) => s !== '')
        .map((s) => `<li>${s}</li>`)
        .join('');
      if (items) pieces.push({ inline: false, html: `<ul>${items}</ul>` });
      continue;
    }
    const html = renderPrepInline(node.content);
    if (html) pieces.push({ inline: true, html });
  }

  return pieces
    .map((piece, index) => {
      const prev = pieces[index - 1];
      const needsBreak = index > 0 && prev.inline && piece.inline;
      return `${needsBreak ? '<br />' : ''}${piece.html}`;
    })
    .join('');
}

/** List-item children are paragraphs; join their text without block markup. */
function renderPrepBlockChildren(children: TipTapNode[] | undefined): string {
  if (!children) return '';
  return children
    .map((child) => renderPrepInline(child.content))
    .filter((s) => s !== '')
    .join(' ');
}

function renderPrepInline(children: TipTapNode[] | undefined): string {
  if (!children) return '';
  return children
    .map((node) => {
      if (node.type === 'hardBreak') return '<br />';
      if (node.type !== 'text') return '';
      let out = escapeText(node.text ?? '');
      for (const mark of node.marks ?? []) {
        if (mark.type === 'bold') out = `<b>${out}</b>`;
        else if (mark.type === 'italic') out = `<i>${out}</i>`;
        // Every other mark is stripped: the server sanitizer would strip it too,
        // and a tag that never survives the write must not be written.
      }
      return out;
    })
    .join('');
}

/**
 * Flatten preparation HTML to plain text for surfaces that cannot render it:
 * the Telegram reminder body (`<ul>` is not a Telegram tag) and the read-only
 * message previews in the Notifications editor.
 */
export function preparationHtmlToPlainText(html: string | null | undefined): string {
  if (!html) return '';
  return decodeEntities(
    html
      // Each item opens its own line. The list wrappers add nothing, so a
      // `<br />` that already separated the lead-in from the list does not
      // become a phantom blank line.
      .replace(/<li[^>]*>/gi, '\n• ')
      .replace(/<\/li\s*>/gi, '')
      .replace(/<\/?(ul|ol)[^>]*>/gi, '')
      .replace(/<br\s*\/?\s*>/gi, '\n')
      .replace(/<[^>]+>/g, ''),
  )
    .replace(/\n{2,}(?=• )/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
